/**
 * Tiny markdown → pdfmake renderer.
 *
 * The Studio AI responses (Anforderungsbewertung, suggestion descriptions,
 * etc.) are markdown-flavoured: `##`/`###` headings, `**bold**` runs,
 * `- ` unordered bullets, `1.` ordered lists. pdfmake renders text nodes
 * literally, so a raw passthrough surfaces `**Foo**` as four asterisks
 * around the word in the PDF — the v1 Studio export looked like raw
 * source. This helper covers the markdown subset the model actually emits
 * and returns a content array the exporter can spread into its document.
 *
 * Out of scope: nested lists, tables, links, code blocks. The Studio
 * prompts don't produce them today; add when they do.
 */

const BOLD_RUN = /\*\*([^*]+)\*\*/g;
const HEADING = /^[ \t]*(#{2,6})\s+(.+)$/;
const ORDERED_ITEM = /^[ \t]*(\d+)\.\s+(.*)$/;
const UNORDERED_ITEM = /^[ \t]*[-*+]\s+(.+)$/;

/**
 * Split a line with inline **bold** runs into pdfmake `text` fragments.
 * Plain strings stay strings; bold runs become `{ text, bold: true }`.
 */
export function inlineMarkdownToRuns(line) {
  if (!line) return '';
  const runs = [];
  let lastIndex = 0;
  let match;
  BOLD_RUN.lastIndex = 0;
  while ((match = BOLD_RUN.exec(line)) !== null) {
    if (match.index > lastIndex) {
      runs.push(line.slice(lastIndex, match.index));
    }
    runs.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) {
    runs.push(line.slice(lastIndex));
  }
  if (runs.length === 0) return '';
  if (runs.length === 1 && typeof runs[0] === 'string') return runs[0];
  return { text: runs };
}

/**
 * Convert a markdown-flavoured string into an array of pdfmake content
 * nodes. The default body style is inherited from the parent docDefinition,
 * so we only emit structural hints (heading style, ul/ol grouping).
 */
export function markdownToPdfmake(raw, {
  headingStyle = 'h3',
  paragraphMargin = [0, 0, 0, 6],
  listMargin = [0, 2, 0, 6],
} = {}) {
  if (raw == null || raw === '') return [];
  const lines = String(raw).split(/\r?\n/);
  const out = [];
  let paragraphBuf = [];
  let bulletBuf = null; // { type: 'ul' | 'ol', items: [] }

  const flushParagraph = () => {
    if (paragraphBuf.length === 0) return;
    const joined = paragraphBuf.join(' ').trim();
    paragraphBuf = [];
    if (!joined) return;
    out.push({ ...wrapInline(inlineMarkdownToRuns(joined)), margin: paragraphMargin });
  };
  const flushBullets = () => {
    if (!bulletBuf || bulletBuf.items.length === 0) { bulletBuf = null; return; }
    const block = { [bulletBuf.type]: bulletBuf.items.map((item) => wrapInline(inlineMarkdownToRuns(item))), margin: listMargin };
    out.push(block);
    bulletBuf = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushBullets();
      continue;
    }
    const headingMatch = line.match(HEADING);
    if (headingMatch) {
      flushParagraph();
      flushBullets();
      out.push({ ...wrapInline(inlineMarkdownToRuns(headingMatch[2].trim())), style: headingStyle, margin: [0, 6, 0, 4] });
      continue;
    }
    const orderedMatch = line.match(ORDERED_ITEM);
    if (orderedMatch) {
      flushParagraph();
      if (!bulletBuf || bulletBuf.type !== 'ol') {
        flushBullets();
        bulletBuf = { type: 'ol', items: [] };
      }
      bulletBuf.items.push(orderedMatch[2].trim());
      continue;
    }
    const unorderedMatch = line.match(UNORDERED_ITEM);
    if (unorderedMatch) {
      flushParagraph();
      if (!bulletBuf || bulletBuf.type !== 'ul') {
        flushBullets();
        bulletBuf = { type: 'ul', items: [] };
      }
      bulletBuf.items.push(unorderedMatch[1].trim());
      continue;
    }
    // Continuation of a paragraph (or first content line).
    flushBullets();
    paragraphBuf.push(line.trim());
  }
  flushParagraph();
  flushBullets();
  return out;
}

/** Strip markdown so the result is a single plain string. */
export function stripMarkdown(raw) {
  if (raw == null) return '';
  // Use `[ \t]*` rather than `\s*` so we don't accidentally swallow the
  // preceding newline (which collapses paragraph breaks).
  return String(raw)
    .replace(/^[ \t]*#{2,6}\s+/gm, '')
    .replace(/^[ \t]*\d+\.\s+/gm, '')
    .replace(/^[ \t]*[-*+]\s+/gm, '')
    .replace(BOLD_RUN, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function wrapInline(run) {
  if (typeof run === 'string') return { text: run };
  if (run && typeof run === 'object' && 'text' in run) return run;
  return { text: String(run ?? '') };
}
